import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, DropdownItem, Spinner, Modal, ModalHeader, ModalBody, UncontrolledCarousel 
} from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

const ReportedIssues = () => {
    document.title = "Reported Issues";
    
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 12,
        totalCount: 0
    });
    const [selectedReport, setSelectedReport] = useState(null);
    const [detailModal, setDetailModal] = useState(false);

    const fetchReports = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            let url = `${API_BASE_URL}/reportProblem?limit=${pagination.limit}&offset=${offset}`;
            
            if (searchText) {
                url += `&searchText=${encodeURIComponent(searchText)}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            
            if (data.data) {
                setReports(data.data.reportsData);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.data.totalCount
                }));
            } else {
                toast.error('Failed to fetch reports data', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
            }
        } catch (error) {
            console.error('Error fetching reports:', error);
            toast.error(error.message || 'Failed to fetch reported issues', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchReports(1);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchText]);

    // Add styles for the report cards
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .report-card {
                transition: all 0.3s ease;
                cursor: pointer;
            }
            
            .report-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 5px 15px rgba(0,0,0,0.1);
            }
            
            .report-text {
                white-space: pre-wrap;
                max-height: 100px;
                overflow: hidden;
                position: relative;
            }
            
            .report-text::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 40px;
                background: linear-gradient(transparent, white);
            }
            
            .report-image {
                max-height: 200px;
                object-fit: contain;
                margin: 10px 0;
            }
            
            .report-timestamp {
                font-size: 0.85rem;
                color: #6c757d;
            }
            
            .report-username {
                font-weight: 500;
                color: #405189;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const getImageUrl = (url) => {
        // Return empty string if url is null/undefined
        if (!url) return '';
        
        // Convert to string to ensure we can use startsWith
        const urlString = String(url);
        
        // Check if it's already a full URL
        if (urlString.startsWith('http')) return urlString;
        
        // Build the media URL
        const baseUrl = process.env.REACT_APP_MEDIA_URL;
        const imagePath = urlString.replace(/^\//, '');
        return `${baseUrl}/media/${imagePath}`;
    };

    const renderReportModal = () => (
        <Modal
            isOpen={detailModal}
            toggle={() => setDetailModal(!detailModal)}
            size="lg"
        >
            <ModalHeader toggle={() => setDetailModal(!detailModal)}>
                Report Details
            </ModalHeader>
            <ModalBody>
                {selectedReport && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="report-username">@{selectedReport.username}</h5>
                            <span className="report-timestamp">
                                {moment(selectedReport.reportedTs).format('DD MMM YYYY, hh:mm A')}
                            </span>
                        </div>
                        <div className="report-text mb-3" style={{ maxHeight: 'none' }}>
                            {selectedReport.problemText}
                        </div>
                        {selectedReport.attachments && (
                            <div className="text-center">
                                <img 
                                    src={getImageUrl(selectedReport.attachments)} 
                                    alt="Report attachment" 
                                    className="img-fluid report-image"
                                />
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>
        </Modal>
    );

    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        
        // Don't show pagination if there are no pages
        if (totalPages <= 0) return null;

        // Function to get page numbers to show
        const getPageNumbers = () => {
            const delta = 2;
            const range = [];
            const rangeWithDots = [];
            let l;

            for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || 
                    (i >= pagination.currentPage - delta && i <= pagination.currentPage + delta)) {
                    range.push(i);
                }
            }

            // Sort and remove duplicates
            const sortedRange = [...new Set(range)].sort((a, b) => a - b);

            for (let i of sortedRange) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">
                        Showing <span className="fw-semibold">{Math.min((pagination.currentPage - 1) * pagination.limit + 1, pagination.totalCount)}-{Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}</span> of <span className="fw-semibold">{pagination.totalCount}</span> Results
                    </div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchReports(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            >
                                <i className="ri-arrow-left-s-line"></i>
                            </Button>
                        </li>
                        
                        {getPageNumbers().map((pageNumber, index) => (
                            <li 
                                key={index} 
                                className={`page-item ${pageNumber === pagination.currentPage ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`}
                            >
                                <Button
                                    className="page-link"
                                    onClick={() => pageNumber !== '...' && fetchReports(pageNumber)}
                                    disabled={pageNumber === '...'}
                                >
                                    {pageNumber}
                                </Button>
                            </li>
                        ))}

                        <li className={`page-item ${pagination.currentPage === totalPages ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchReports(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === totalPages}
                            >
                                <i className="ri-arrow-right-s-line"></i>
                            </Button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    return (
        <React.Fragment>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Reported Issues" pageTitle="Issues" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="border-bottom-dashed">
                                    <Row className="g-3">
                                        <Col xxl={3} lg={6}>
                                            <div className="search-box">
                                                <Input
                                                    type="text"
                                                    className="form-control search"
                                                    placeholder="Search reports..."
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        {reports.map((report) => (
                                            <Col md={6} lg={4} key={report._id}>
                                                <Card 
                                                    className="report-card mb-3"
                                                    onClick={() => {
                                                        setSelectedReport(report);
                                                        setDetailModal(true);
                                                    }}
                                                >
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h5 className="report-username">@{report.username}</h5>
                                                            <span className="report-timestamp">
                                                                {moment(report.reportedTs).format('DD MMM YYYY')}
                                                            </span>
                                                        </div>
                                                        <div className="report-text">
                                                            {report.problemText}
                                                        </div>
                                                        {report.attachments && (
                                                            <div className="text-center mt-2">
                                                                <img 
                                                                    src={getImageUrl(report.attachments)} 
                                                                    alt="Report attachment" 
                                                                    className="img-fluid report-image"
                                                                />
                                                            </div>
                                                        )}
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                        {reports.length === 0 && (
                                            <Col xs={12}>
                                                <div className="text-center py-4 text-muted">
                                                    <i className="ri-information-line fs-4 me-1 align-middle"></i>
                                                    No reported issues found
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                    {reports.length > 0 && renderPagination()}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            {renderReportModal()}
            {loading && (
                <div className="overlay">
                    <div className="spinner-container">
                        <Spinner color="primary" />
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default ReportedIssues;